import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsMegaphone } from 'react-icons/bs';

// utils
import { fToNow } from '../../utils/formatTime';

// components
import Iconify from '../../components/Iconify';

// redux
import actionMarcarNotificacoesComoLidas from 'src/actions/notificacoes/actionMarcarNotificacoesComoLidas';
import { getListaNotificacoes } from 'src/redux/slices/notificacoes';
import useAuth from 'src/hooks/useAuth';
// import { PATH_DASHBOARD } from 'src/routes/paths'; // Unused in original logic shown (commented out)
import { changeOpenDetalhesChamado, getVerChamado } from 'src/redux/slices/chamados';
import actionLerNotificacao from 'src/actions/notificacoes/actionLerNotificacao';

// shadcn/ui
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Button } from 'src/@/components/ui/button';
import { Badge } from 'src/@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from 'src/@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import { cn } from 'src/@/lib/utils';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('navigation');
  const { notificacoesNaoLidas, novidades } = useSelector((state) => state.notificacoes);
  const totalUnRead = notificacoesNaoLidas.length;

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleMarkAllAsRead = async () => {
    try {
      await actionMarcarNotificacoesComoLidas(user.id);
      dispatch(getListaNotificacoes(user.id));
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickNotification = async (notification) => {
    if (notification.tipo === "chamados") {
      setLoading(true);
      if (notification.chamado_id) {
        await actionLerNotificacao(user.id, { notificacaoId: notification._id });
        dispatch(getVerChamado(notification.chamado_id));
        dispatch(changeOpenDetalhesChamado(true));
        setLoading(false);
        setOpen(false);
        dispatch(getListaNotificacoes(user.id));
      }
    } else if (notification.tipo === "tarefas") {
      setLoading(true);
      await actionLerNotificacao(user.id, { notificacaoId: notification._id });
      // Deep-link: a pagina de Tarefas abre o drawer da tarefa indicada.
      navigate(notification.tarefa_id ? `/dashboard/tarefas?tarefa=${notification.tarefa_id}` : '/dashboard/tarefas');
      setLoading(false);
      setOpen(false);
      dispatch(getListaNotificacoes(user.id));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-all relative",
            open && "bg-gray-100 text-[#7C3AED]"
          )}
        >
          <Iconify icon="solar:bell-bing-bold-duotone" width={22} height={22} />
          {totalUnRead > 0 && (
            <Badge
              className="absolute -top-1.5 -right-1.5 h-4 w-4 flex items-center justify-center p-0 text-[9px] bg-red-500 hover:bg-red-600 border-2 border-white"
            >
              {totalUnRead}
            </Badge>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[380px] p-0 rounded-2xl border-gray-100 shadow-xl overflow-hidden bg-white/80 backdrop-blur-md">
        <Tabs defaultValue="notifications" className="w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100/50 bg-white/50 backdrop-blur-sm sticky top-0 z-10">
            <TabsList className="bg-gray-100/50 h-9 p-1">
              <TabsTrigger value="notifications" className="text-xs px-3 h-7 data-[state=active]:bg-white data-[state=active]:text-[#7C3AED] data-[state=active]:shadow-sm">
                {t('notifications.title')}
              </TabsTrigger>
              <TabsTrigger value="novidades" className="text-xs px-3 h-7 data-[state=active]:bg-white data-[state=active]:text-[#7C3AED] data-[state=active]:shadow-sm">
                {t('notifications.news')}
              </TabsTrigger>
            </TabsList>

            {totalUnRead > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-[#7C3AED] hover:text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-lg"
                      onClick={handleMarkAllAsRead}
                    >
                      <Iconify icon="solar:check-read-linear" width={18} height={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {t('notifications.markAllRead')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <TabsContent value="notifications" className="m-0 focus-visible:ring-0 focus-visible:outline-none">
            {loading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="w-8 h-8 rounded-full border-2 border-[#7C3AED] border-t-transparent animate-spin" />
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                {notificacoesNaoLidas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] text-gray-400 p-6 text-center">
                    <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                      <Iconify icon="solar:bell-bing-linear" width={32} className="text-gray-300" />
                    </div>
                    <span className="text-sm font-medium text-gray-600">{t('notifications.emptyTitle')}</span>
                    <span className="text-xs mt-1 text-gray-500">{t('notifications.emptyDescription')}</span>
                  </div>
                ) : (
                  <div className="flex flex-col p-2 gap-1">
                    {notificacoesNaoLidas.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={() => handleClickNotification(notification)}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="novidades" className="m-0 focus-visible:ring-0 focus-visible:outline-none">
            <ScrollArea className="h-[400px]">
              {(!novidades || novidades.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-gray-400 p-6 text-center">
                  <div className="h-16 w-16 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-full flex items-center justify-center mb-4 border border-indigo-100">
                    <Iconify icon="solar:star-fall-bold-duotone" width={32} className="text-[#7C3AED]" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">{t('notifications.newsSoon')}</span>
                  <span className="text-xs mt-1 text-gray-500">{t('notifications.newsSoonDescription')}</span>
                </div>
              ) : (
                <div className="flex flex-col p-3 gap-3">
                  {novidades.map((novidade) => (
                    <NovidadeItem key={novidade._id} novidade={novidade} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>


      </PopoverContent>
    </Popover>
  );
}

// ----------------------------------------------------------------------

function NovidadeItem({ novidade }) {
  const { t } = useTranslation('navigation');
  const getIcon = (type) => {
    switch (type) {
      case 'feature': return 'solar:star-fall-bold-duotone';
      case 'warning': return 'solar:danger-triangle-bold-duotone';
      case 'info': default: return 'solar:info-circle-bold-duotone';
    }
  };

  const getColors = (type) => {
    switch (type) {
      case 'feature': return 'bg-purple-50 text-purple-600 border-purple-100';
      case 'warning': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'info': default: return 'bg-blue-50 text-blue-600 border-blue-100';
    }
  };

  const colors = getColors(novidade.tipo);

  const Content = (
    <div className={cn("p-4 rounded-xl border relative transition-all hover:shadow-sm bg-white", colors.replace('bg-', 'bg-opacity-5 border-opacity-60'))}>
      <div className="flex items-start gap-3">
        <div className={cn("flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center", colors)}>
          <Iconify icon={getIcon(novidade.tipo)} width={18} />
        </div>
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-1">
            <span className={cn("text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white border border-opacity-50", colors.split(' ')[2])}>
              {novidade.tipo === 'feature' ? t('notifications.badges.feature') : novidade.tipo === 'warning' ? t('notifications.badges.warning') : t('notifications.badges.info')}
            </span>
            <span className="text-[10px] text-gray-400">{fToNow(novidade.data_criacao)}</span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1 leading-tight">{novidade.titulo}</h4>
          <p className="text-xs text-gray-600 leading-relaxed font-normal">
            {novidade.mensagem}
          </p>
          {/* Se houver imagem */}
          {novidade.imagem && (
            <div className="mt-3 rounded-lg overflow-hidden border border-gray-100">
              <img src={novidade.imagem} alt={novidade.titulo} className="w-full h-32 object-cover" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (novidade.link) {
    return (
      <Link to={novidade.link} className="block no-underline">
        {Content}
      </Link>
    );
  }

  return Content;
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    createdAt: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
    lido: PropTypes.bool,
    titulo: PropTypes.string,
    mensagem: PropTypes.string,
    data_criacao: PropTypes.string,
  }),
  onClick: PropTypes.func,
};

function NotificationItem({ notification, onClick }) {
  const { avatar, titulo } = renderContent(notification);

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-start w-full p-3 text-left transition-all rounded-xl hover:bg-gray-50 group border border-transparent",
        !notification.lido && "bg-[#7C3AED]/5 border-[#7C3AED]/10 hover:bg-[#7C3AED]/10"
      )}
    >
      <div className="flex-shrink-0 mt-1 mr-3">
        {avatar}
      </div>

      <div className="flex-grow min-w-0">
        <div className="text-sm text-gray-900">
          {titulo}
        </div>

        <div className="flex items-center mt-1.5 text-[10px] text-gray-400 font-medium">
          <Iconify icon="solar:clock-circle-linear" width={12} height={12} className="mr-1" />
          {fToNow(notification.data_criacao)}
        </div>
      </div>

      {!notification.lido && (
        <div className="flex-shrink-0 ml-2 mt-2">
          <div className="w-2 h-2 rounded-full bg-[#7C3AED]" />
        </div>
      )}
    </button>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const titulo = (
    <span className="block text-sm leading-snug">
      <span className="font-semibold text-gray-900">{notification.titulo}</span>
      <span className="text-gray-600 block text-xs mt-0.5 font-normal line-clamp-2">
        {noCase(notification.mensagem)}
      </span>
    </span>
  );

  if (notification.tipo === 'chamados') {
    return {
      avatar: (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600">
          <Iconify icon="solar:ticket-sale-bold-duotone" width={18} />
        </div>
      ),
      titulo,
    };
  }

  if (notification.tipo === 'tarefas') {
    return {
      avatar: (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-50 text-violet-600">
          <Iconify icon="solar:checklist-bold-duotone" width={18} />
        </div>
      ),
      titulo,
    };
  }

  if (notification.tipo === 'chat_msg') { // Example for other types if needed
    return {
      avatar: (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50 text-green-600">
          <Iconify icon="solar:chat-round-dots-bold-duotone" width={18} />
        </div>
      ),
      titulo,
    };
  }

  return {
    avatar: notification.avatar ? (
      <Avatar className="w-8 h-8">
        <AvatarImage src={notification.avatar} alt={notification.titulo} />
        <AvatarFallback>{notification.titulo?.charAt(0)}</AvatarFallback>
      </Avatar>
    ) : (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500">
        <Iconify icon="solar:bell-linear" width={18} />
      </div>
    ),
    titulo,
  };
}
